import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { AppShell } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CustomerAccountPage } from './pages/CustomerAccountPage';
import { CustomersPage } from './pages/CustomersPage';
import { DashboardPage } from './pages/DashboardPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { LiveMapPage } from './pages/LiveMapPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { ProductsPage } from './pages/ProductsPage';
import { ReportsPage } from './pages/ReportsPage';
import { RouteEditorPage } from './pages/RouteEditorPage';
import { RoutesPage } from './pages/RoutesPage';
import { SettingsPage } from './pages/SettingsPage';
import { StockPage } from './pages/StockPage';
import { UsersPage } from './pages/UsersPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { WarehousesPage } from './pages/WarehousesPage';
import { LoginPage } from './pages/LoginPage';
import { ThemeModeProvider } from './theme-mode';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function FullPageSpinner() {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

/**
 * Field staff have valid credentials but no business in the panel. Telling them
 * so and offering a way out is necessary — redirecting to /login would bounce
 * them straight back here, since they are signed in.
 */
function WrongRoleNotice() {
  const { t } = useTranslation();
  const { logout } = useAuth();

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Stack spacing={2} sx={{ maxWidth: 400 }}>
        <Alert severity="warning">{t('auth.errors.forbiddenRole')}</Alert>
        <Button variant="contained" onClick={() => void logout()}>
          {t('common.logout')}
        </Button>
      </Stack>
    </Box>
  );
}

/** Only `admin` reaches the panel; field staff use the mobile app. */
function RequireAdmin() {
  const { user, initialising } = useAuth();
  const location = useLocation();

  if (initialising) {
    return <FullPageSpinner />;
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (user.role !== 'admin') {
    return <WrongRoleNotice />;
  }
  return <AppShell />;
}

export function App() {
  return (
    <ThemeModeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/* An outer boundary covers login and the shell itself. */}
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<RequireAdmin />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/warehouses" element={<WarehousesPage />} />
              <Route path="/vehicles" element={<VehiclesPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/stock" element={<StockPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/documents/:id" element={<DocumentDetailPage />} />
              <Route path="/payments" element={<PaymentsPage />} />
              <Route path="/customers/:customerId/account" element={<CustomerAccountPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/routes" element={<RoutesPage />} />
              <Route path="/routes/:id" element={<RouteEditorPage />} />
              <Route path="/live" element={<LiveMapPage />} />
              <Route path="/reports" element={<ReportsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeModeProvider>
  );
}
